import type { ReactNode } from 'react';
import './avatar.css';

export type AvatarTone =
  'cyan' | 'pink' | 'amber' | 'cyan-soft' | 'pink-soft' | 'green' | 'neutral';
export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  /** Already computed by the server, so every surface shows the same monogram. */
  initials: string;
  /**
   * The name behind the monogram. Used to pick a stable tone and to give screen
   * readers something better than two letters.
   */
  name: string;
  tone?: AvatarTone;
  size?: AvatarSize;
  children?: ReactNode;
}

/** The prototype's cycle: three solid fills, then two washes. */
const TONES: AvatarTone[] = ['cyan', 'pink', 'amber', 'cyan-soft', 'pink-soft'];

/**
 * A monogram tile.
 *
 * The tone is derived from the name rather than stored or random, so a person keeps
 * the same colour on every screen and across reloads — a colour that changed on each
 * render would read as a different person.
 */
export function Avatar({ initials, name, tone, size = 'md', children }: AvatarProps) {
  return (
    <span
      className="avatar"
      data-tone={tone ?? toneFor(name)}
      data-size={size}
      aria-hidden={children === undefined ? undefined : true}
    >
      <span className="avatarInitials">{initials}</span>
      {children}
    </span>
  );
}

/** A stable index into the tone list from the name's characters. */
function toneFor(name: string): AvatarTone {
  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) % 1_000_003;
  return TONES[hash % TONES.length]!;
}
